import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  RefreshCw, Plus, Pause, Play, FileText, Trash2, Calendar,
  DollarSign, User, Briefcase, Clock, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import RecurringInvoiceModal from '../components/invoices/RecurringInvoiceModal';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function RecurringInvoices() {
  const [recurringInvoices, setRecurringInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterActive, setFilterActive] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;
  const navigate = useNavigate();

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  // Action states
  const [generating, setGenerating] = useState(null);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    fetchRecurringInvoices();
  }, [currentPage, filterActive]);

  const fetchRecurringInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = {
        page: currentPage,
        page_size: pageSize
      };

      if (filterActive !== 'all') {
        params.is_active = filterActive === 'active';
      }

      const response = await axios.get(`${API_URL}/api/recurring-invoices`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setRecurringInvoices(response.data.items || []);
      setTotalItems(response.data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching recurring invoices:', err);
      setError('Failed to load recurring invoices');
      setRecurringInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNow = async (id) => {
    try {
      setGenerating(id);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/recurring-invoices/${id}/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Invoice ${response.data.invoice_number} generated successfully!`);
      fetchRecurringInvoices();
    } catch (err) {
      console.error('Error generating invoice:', err);
      alert(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(null);
    }
  };

  const handleToggleActive = async (id, currentlyActive) => {
    try {
      setToggling(id);
      const token = localStorage.getItem('token');
      const endpoint = currentlyActive ? 'pause' : 'resume';
      await axios.post(
        `${API_URL}/api/recurring-invoices/${id}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchRecurringInvoices();
    } catch (err) {
      console.error('Error toggling recurring invoice:', err);
      alert(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recurring invoice?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/recurring-invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRecurringInvoices();
    } catch (err) {
      console.error('Error deleting recurring invoice:', err);
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingInvoice(null);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingInvoice(null);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setEditingInvoice(null);
    fetchRecurringInvoices();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getFrequencyLabel = (frequency) => {
    return frequency === 'weekly' ? 'Weekly' : 'Monthly';
  };

  const getDayLabel = (frequency, day) => {
    if (frequency === 'weekly') {
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      return days[day] || `Day ${day}`;
    }
    return `Day ${day}`;
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <RefreshCw className="text-blue-400" />
            Recurring Invoices
          </h1>
          <p className="text-slate-400 mt-1">
            Manage automated billing schedules
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/invoices')}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          >
            View All Invoices
          </button>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus size={18} />
            New Recurring Invoice
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFilterActive('all')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filterActive === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilterActive('active')}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
            filterActive === 'active'
              ? 'bg-green-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          <CheckCircle size={16} />
          Active
        </button>
        <button
          onClick={() => setFilterActive('paused')}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
            filterActive === 'paused'
              ? 'bg-amber-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          <Pause size={16} />
          Paused
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 p-4 rounded-lg mb-6 flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="animate-spin mx-auto text-blue-400 mb-4" size={32} />
            <p className="text-slate-400">Loading recurring invoices...</p>
          </div>
        ) : recurringInvoices.length === 0 ? (
          <div className="p-12 text-center">
            <RefreshCw className="mx-auto text-slate-600 mb-4" size={48} />
            <p className="text-slate-400">No recurring invoices found</p>
            <button
              onClick={handleCreate}
              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg inline-flex items-center gap-2"
            >
              <Plus size={18} />
              Create Your First Recurring Invoice
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Client</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Description</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Frequency</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Next Invoice</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Generated</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {recurringInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-slate-700/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-slate-400" />
                      <div>
                        <div className="font-medium">{invoice.contact_name || 'Unknown'}</div>
                        {invoice.case_name && (
                          <div className="text-xs text-slate-400 flex items-center gap-1">
                            <Briefcase size={12} />
                            {invoice.case_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300 max-w-xs truncate">
                    {invoice.description || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-green-400">
                      {formatCurrency(invoice.amount)}
                    </span>
                    {invoice.tax_rate > 0 && (
                      <span className="text-xs text-slate-400 ml-1">
                        +{invoice.tax_rate}% tax
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-blue-400" />
                      <div>
                        <div>{getFrequencyLabel(invoice.frequency)}</div>
                        <div className="text-xs text-slate-400">
                          {getDayLabel(invoice.frequency, invoice.billing_day)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-amber-400" />
                      {formatDate(invoice.next_invoice_date)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {invoice.is_active ? (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-sm flex items-center gap-1 w-fit">
                        <CheckCircle size={14} />
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-sm flex items-center gap-1 w-fit">
                        <Pause size={14} />
                        Paused
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {invoice.invoices_generated || 0} invoices
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleGenerateNow(invoice.id)}
                        disabled={generating === invoice.id || !invoice.is_active}
                        className="p-2 bg-blue-600 hover:bg-blue-500 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Generate Invoice Now"
                      >
                        {generating === invoice.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <FileText size={16} />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleActive(invoice.id, invoice.is_active)}
                        disabled={toggling === invoice.id}
                        className={`p-2 rounded transition-colors ${
                          invoice.is_active
                            ? 'bg-amber-600 hover:bg-amber-500'
                            : 'bg-green-600 hover:bg-green-500'
                        }`}
                        title={invoice.is_active ? 'Pause' : 'Resume'}
                      >
                        {toggling === invoice.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : invoice.is_active ? (
                          <Pause size={16} />
                        ) : (
                          <Play size={16} />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(invoice)}
                        className="p-2 bg-slate-600 hover:bg-slate-500 rounded transition-colors"
                        title="Edit"
                      >
                        <DollarSign size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(invoice.id)}
                        className="p-2 bg-red-600 hover:bg-red-500 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-slate-400">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <RecurringInvoiceModal
          invoice={editingInvoice}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}

export default RecurringInvoices;
